import { assertType, Slot } from '@blocksuite/global/utils';
import { generateKeyBetween } from 'fractional-indexing';
import {} from '../../_common/types.js';
import { last, nToLast } from '../../_common/utils/iterable.js';
import { matchFlavours } from '../../_common/utils/model.js';
import { GroupElement } from '../../surface-block/elements/index.js';
import { Bound } from '../../surface-block/utils/bound.js';
import { GROUP_ROOT } from '../elements/group/consts.js';
import { SurfaceElement } from '../elements/surface-element.js';
import { GridManager } from '../grid.js';
import { compare, getGroupParent } from './group-manager.js';
import { getElementIndex, getLayerZIndex, insertToOrderedArray, isInRange, removeFromOrderedArray, ungroupIndex, updateLayersIndex, } from './layer-utils.js';
export class LayerManager {
    constructor(elements) {
        this.slots = {
            layerUpdated: new Slot(),
        };
        this.blocksGrid = new GridManager();
        this.framesGrid = new GridManager();
        if (elements) {
            this.init(elements);
        }
    }
    init(elements) {
        this.canvasElements = [];
        this.blocks = [];
        this.frames = [];
        elements.forEach(element => {
            if (element instanceof SurfaceElement) {
                this.canvasElements.push(element);
            }
            else if (matchFlavours(element, ['affine:frame'])) {
                this.framesGrid.add(element);
                this.frames.push(element);
            }
            else {
                this.blocksGrid.add(element);
                this.blocks.push(element);
            }
        });
        this.canvasElements.sort(compare);
        this.frames.sort(compare);
        this.blocks.sort(compare);
        this._initLayers();
        this._buildCanvasLayers();
    }
    _initLayers() {
        let blockIdx = 0;
        let canvasIdx = 0;
        const layers = [];
        let curLayer;
        let currentCSSZindex = 1;
        const pushCurLayer = () => {
            if (curLayer) {
                curLayer.indexes = [
                    getElementIndex(curLayer.elements[0]),
                    getElementIndex(last(curLayer.elements)),
                ];
                curLayer.zIndexes =
                    curLayer.type === 'block'
                        ? [currentCSSZindex, currentCSSZindex + curLayer.elements.length]
                        : currentCSSZindex;
                layers.push(curLayer);
                currentCSSZindex +=
                    curLayer.type === 'block' ? curLayer.elements.length + 1 : 1;
            }
        };
        const addLayer = (type) => {
            pushCurLayer();
            curLayer =
                type === 'canvas'
                    ? {
                        type,
                        indexes: [LayerManager.INITAL_INDEX, LayerManager.INITAL_INDEX],
                        zIndexes: 0,
                        set: new Set(),
                        elements: [],
                        bound: new Bound(),
                    }
                    : {
                        type,
                        indexes: [LayerManager.INITAL_INDEX, LayerManager.INITAL_INDEX],
                        zIndexes: [0, 0],
                        set: new Set(),
                        elements: [],
                    };
        };
        while (blockIdx < this.blocks.length ||
            canvasIdx < this.canvasElements.length) {
            const curBlock = this.blocks[blockIdx];
            const curCanvas = this.canvasElements[canvasIdx];
            if (!curBlock && !curCanvas) {
                break;
            }
            if (!curBlock) {
                if (curLayer?.type !== 'canvas') {
                    addLayer('canvas');
                }
                assertType(curLayer);
                const remains = this.canvasElements.slice(canvasIdx);
                curLayer.elements = curLayer.elements.concat(remains);
                remains.forEach(element => curLayer.set.add(element));
                break;
            }
            if (!curCanvas) {
                if (curLayer?.type !== 'block') {
                    addLayer('block');
                }
                assertType(curLayer);
                const remains = this.blocks.slice(blockIdx);
                curLayer.elements = curLayer.elements.concat(remains);
                remains.forEach(block => curLayer.set.add(block));
                break;
            }
            const order = compare(curBlock, curCanvas);
            switch (order) {
                case -1:
                    if (curLayer?.type !== 'block') {
                        addLayer('block');
                    }
                    assertType(curLayer);
                    curLayer.set.add(curBlock);
                    curLayer.elements.push(curBlock);
                    ++blockIdx;
                    break;
                case 1:
                    if (curLayer?.type !== 'canvas') {
                        addLayer('canvas');
                    }
                    assertType(curLayer);
                    curLayer.set.add(curCanvas);
                    curLayer.elements.push(curCanvas);
                    ++canvasIdx;
                    break;
                case 0:
                    if (!curLayer) {
                        addLayer('block');
                    }
                    if (curLayer.type === 'block') {
                        curLayer.set.add(curBlock);
                        curLayer.elements.push(curBlock);
                        ++blockIdx;
                    }
                    else {
                        curLayer.set.add(curCanvas);
                        curLayer.elements.push(curCanvas);
                        ++canvasIdx;
                    }
                    break;
            }
        }
        if (curLayer && curLayer.elements.length) {
            pushCurLayer();
        }
        this.layers = layers;
    }
    _insertIntoLayer(target, type) {
        if (this.layers.length === 0) {
            this._initLayers();
            return;
        }
        const layers = this.layers;
        let cur = layers.length - 1;
        const addToLayer = (layer, element, position) => {
            assertType(layer);
            assertType(element);
            if (position === 'tail') {
                layer.elements.push(element);
            }
            else {
                layer.elements.splice(position, 0, element);
            }
            layer.set.add(element);
            if (position === 'tail' ||
                position === 0 ||
                position === layer.elements.length - 1) {
                layer.indexes = [
                    getElementIndex(layer.elements[0]),
                    getElementIndex(last(layer.elements)),
                ];
            }
        };
        const createLayer = (type, targets, curZIndex) => {
            const newLayer = {
                type,
                set: new Set(targets),
                indexes: [
                    getElementIndex(targets[0]),
                    getElementIndex(last(targets)),
                ],
                zIndexes: type === 'block'
                    ? [curZIndex + 1, curZIndex + targets.length]
                    : curZIndex + 1,
                elements: targets,
            };
            return newLayer;
        };
        if (compare(target, last(last(this.layers).elements)) > 0) {
            const layer = last(this.layers);
            if (layer?.type === type) {
                addToLayer(layer, target, 'tail');
                updateLayersIndex(layers, cur);
            }
            else {
                this.layers.push(createLayer(type, [target], getLayerZIndex(layers, layers.length - 1)));
            }
        }
        else {
            while (cur > -1) {
                const layer = layers[cur];
                const layerElements = layer.elements;
                if (isInRange([layerElements[0], last(layerElements)], target)) {
                    const insertIdx = layerElements.findIndex((_, idx) => {
                        const pre = layerElements[idx - 1];
                        return (compare(target, layerElements[idx]) < 0 &&
                            (!pre || compare(target, pre) >= 0));
                    });
                    if (layer.type === type) {
                        addToLayer(layer, target, insertIdx);
                        updateLayersIndex(layers, cur);
                    }
                    else {
                        const splicedElements = layer.elements.splice(insertIdx);
                        layer.set = new Set(layer.elements);
                        layers.splice(cur + 1, 0, createLayer(layer.type, splicedElements, 1));
                        layers.splice(cur + 1, 0, createLayer(type, [target], 1));
                        updateLayersIndex(layers, cur);
                    }
                    break;
                }
                else {
                    const nextLayer = layers[cur - 1];
                    if (!nextLayer || compare(target, last(nextLayer.elements)) >= 0) {
                        if (layer.type === type) {
                            addToLayer(layer, target, 0);
                            updateLayersIndex(layers, cur);
                        }
                        else {
                            if (nextLayer) {
                                addToLayer(nextLayer, target, 'tail');
                                updateLayersIndex(layers, cur - 1);
                            }
                            else {
                                layers.unshift(createLayer(type, [target], 1));
                                updateLayersIndex(layers, 0);
                            }
                        }
                        break;
                    }
                }
                --cur;
            }
        }
    }
    _removeFromLayer(target, type) {
        const layers = this.layers;
        const index = layers.findIndex(layer => {
            if (layer.type !== type)
                return false;
            assertType(layer);
            assertType(target);
            if (layer.set.has(target)) {
                layer.set.delete(target);
                const idx = layer.elements.indexOf(target);
                if (idx !== -1) {
                    layer.elements.splice(layer.elements.indexOf(target), 1);
                    if (layer.elements.length) {
                        layer.indexes = [
                            getElementIndex(layer.elements[0]),
                            getElementIndex(last(layer.elements)),
                        ];
                    }
                }
                return true;
            }
            return false;
        });
        if (index === -1)
            return;
        const isDeletedAtEdge = index === 0 || index === layers.length - 1;
        if (layers[index].set.size === 0) {
            if (isDeletedAtEdge) {
                layers.splice(index, 1);
                if (layers[index]) {
                    updateLayersIndex(layers, index);
                }
            }
            else {
                const lastLayer = layers[index - 1];
                const nextLayer = layers[index + 1];
                lastLayer.elements = lastLayer.elements.concat(nextLayer.elements);
                lastLayer.set = new Set(lastLayer.elements);
                layers.splice(index, 2);
                updateLayersIndex(layers, index - 1);
            }
            return;
        }
        updateLayersIndex(layers, index);
    }
    _buildCanvasLayers() {
        const canvasLayers = this.layers
            .filter((layer) => layer.type === 'canvas')
            .map(layer => {
            return {
                set: layer.set,
                elements: layer.elements,
                zIndexes: layer.zIndexes,
                indexes: layer.indexes,
            };
        });
        if (!canvasLayers.length || last(this.layers)?.type !== 'canvas') {
            canvasLayers.push({
                set: new Set(),
                elements: [],
                zIndexes: 0,
                indexes: [LayerManager.INITAL_INDEX, LayerManager.INITAL_INDEX],
            });
        }
        this.canvasLayers = canvasLayers;
    }
    /**
     * @returns a boolean value to indicate whether the layers have been updated
     */
    _updateLayer(element, props) {
        let updateType = undefined;
        const indexChanged = !props || 'index' in props;
        const updateArray = (array, element) => {
            if (!indexChanged)
                return;
            removeFromOrderedArray(array, element);
            insertToOrderedArray(array, element);
        };
        if (element instanceof SurfaceElement) {
            updateType = 'canvas';
            updateArray(this.canvasElements, element);
            if (element instanceof GroupElement && indexChanged) {
                element.childElements.forEach(child => child && this._updateLayer(child));
            }
        }
        else if (matchFlavours(element, ['affine:frame'])) {
            updateArray(this.frames, element);
            this.framesGrid.add(element);
        }
        else {
            updateType = 'block';
            updateArray(this.blocks, element);
            this.blocksGrid.add(element);
        }
        if (updateType && indexChanged) {
            this._removeFromLayer(element, updateType);
            this._insertIntoLayer(element, updateType);
            return true;
        }
        return false;
    }
    add(element) {
        let insertType = undefined;
        if (element instanceof SurfaceElement) {
            insertType = 'canvas';
            insertToOrderedArray(this.canvasElements, element);
            if (element instanceof GroupElement) {
                element.childElements.forEach(child => child && this._updateLayer(child));
            }
        }
        else if (matchFlavours(element, ['affine:frame'])) {
            insertToOrderedArray(this.frames, element);
            this.framesGrid.add(element);
        }
        else {
            insertType = 'block';
            insertToOrderedArray(this.blocks, element);
            this.blocksGrid.add(element);
        }
        if (insertType) {
            this._insertIntoLayer(element, insertType);
            this._buildCanvasLayers();
            this.slots.layerUpdated.emit();
        }
    }
    delete(element) {
        let deleteType = undefined;
        if (element instanceof SurfaceElement) {
            deleteType = 'canvas';
            removeFromOrderedArray(this.canvasElements, element);
        }
        else if (matchFlavours(element, ['affine:frame'])) {
            removeFromOrderedArray(this.frames, element);
            this.framesGrid.remove(element);
        }
        else {
            deleteType = 'block';
            removeFromOrderedArray(this.blocks, element);
            this.blocksGrid.remove(element);
        }
        if (deleteType) {
            this._removeFromLayer(element, deleteType);
            this._buildCanvasLayers();
            this.slots.layerUpdated.emit();
        }
    }
    update(element, props) {
        if (this._updateLayer(element, props)) {
            this._buildCanvasLayers();
            this.slots.layerUpdated.emit();
        }
    }
    getCanvasLayers() {
        return this.canvasLayers;
    }
    generateIndex(batch, type = 'canvas') {
        if (batch === 'frame') {
            const lastFrame = last(this.frames);
            return lastFrame
                ? generateKeyBetween(ungroupIndex(getElementIndex(lastFrame)), null)
                : LayerManager.INITAL_INDEX;
        }
        else {
            if (type === 'canvas') {
                const lastIndex = last(this.layers)?.indexes[1];
                return lastIndex
                    ? generateKeyBetween(ungroupIndex(lastIndex), null)
                    : LayerManager.INITAL_INDEX;
            }
            const lastLayer = last(this.layers);
            if (!lastLayer)
                return LayerManager.INITAL_INDEX;
            assertType(lastLayer);
            if (lastLayer.type === 'canvas') {
                const secondLastLayer = nToLast(this.layers, 2);
                const secondLastLayerIndex = secondLastLayer
                    ? ungroupIndex(secondLastLayer.indexes[1])
                    : null;
                return generateKeyBetween(secondLastLayerIndex, secondLastLayerIndex && secondLastLayerIndex >= lastLayer.indexes[0]
                    ? null
                    : lastLayer.indexes[0]);
            }
            return generateKeyBetween(lastLayer.indexes[1], null);
        }
    }
    getReorderedIndex(element, direction) {
        const group = getGroupParent(element);
        const isFrameBlock = element.flavour === 'affine:frame';
        let elements;
        if (group !== GROUP_ROOT) {
            elements = group.childElements.filter(element => (element?.flavour === 'affine:frame') ===
                isFrameBlock);
            elements.sort(compare);
        }
        else if (isFrameBlock) {
            elements = this.frames;
        }
        else {
            elements = this.layers.reduce((pre, current) => pre.concat(current.elements.filter(element => getGroupParent(element) == GROUP_ROOT)), []);
        }
        const currentIdx = elements.indexOf(element);
        switch (direction) {
            case 'forward':
            case 'front':
                if (currentIdx === -1 || currentIdx === elements.length - 1)
                    return element.index;
                {
                    const next = direction === 'forward'
                        ? elements[currentIdx + 1]
                        : elements[elements.length - 1];
                    const next2 = direction === 'forward' ? elements[currentIdx + 2] : null;
                    return generateKeyBetween(next.index, next2?.index
                        ? next.index < next2.index
                            ? next2.index
                            : null
                        : null);
                }
            case 'backward':
            case 'back':
                if (currentIdx === -1 || currentIdx === 0)
                    return element.index;
                {
                    const pre = direction === 'backward' ? elements[currentIdx - 1] : elements[0];
                    const pre2 = direction === 'backward' ? elements[currentIdx - 2] : null;
                    return generateKeyBetween(pre2?.index ?? null, pre.index);
                }
        }
    }
}
LayerManager.INITAL_INDEX = 'a0';
//# sourceMappingURL=layer-manager.js.map